import { useNavigate } from 'react-router-dom';
import { Clapperboard, Megaphone, Telescope, ArrowRight, Sparkles, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/components/ui/utils';
import { AppTopBar } from './AppTopBar';
import { SectionHeader } from './SectionHeader';
import { InternBadge } from './shared/Badge';
import { PRODUCT_NAME } from '../config';

type TrackId = 'creative' | 'social' | 'research';

interface Discipline {
  id: TrackId;
  name: string;
  tagline: string;
  description: string;
  icon: LucideIcon;
}

// The disciplines XYZ runs interns across. The logged-in intern's own track
// (user_profiles.intern_track) is highlighted; content itself is scoped
// per-person, so every intern opens the same dashboard route and sees only
// the work assigned to them.
const DISCIPLINES: Discipline[] = [
  {
    id: 'creative',
    name: 'Creative',
    tagline: 'Video, design & content',
    description:
      'Cut hero videos, build launch creative, and ship assets across Reels, TikTok, and the brands we run.',
    icon: Clapperboard,
  },
  {
    id: 'social',
    name: 'Social',
    tagline: 'Posting, community & engagement',
    description:
      'Own the posting calendar, grow the community, and keep every channel active across our brands.',
    icon: Megaphone,
  },
  {
    id: 'research',
    name: 'Data / Research',
    tagline: 'Market & brand intelligence',
    description:
      'Gather competitive intel, track the numbers, and turn raw data into briefs the team can act on.',
    icon: Telescope,
  },
];

const DASHBOARD_ROUTE = '/intern/dashboard';

export function InternHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userProfile } = usePermissions();

  const userName =
    userProfile?.display_name || user?.email?.split('@')[0] || 'there';
  const firstName = userName.split(' ')[0];

  const track = userProfile?.intern_track ?? null;
  const myDiscipline = DISCIPLINES.find((d) => d.id === track) ?? null;

  const workspaceName = myDiscipline ? `${myDiscipline.name} Intern` : 'Your Workspace';
  const workspaceTagline =
    myDiscipline?.tagline ?? 'Daily checklist, live projects & skills';
  const workspaceDescription =
    myDiscipline?.description ??
    'Your daily checklist, the projects assigned to you, and the skills to build — all in one place.';
  const WorkspaceIcon = myDiscipline?.icon ?? Sparkles;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-warm-white)' }}>
      <AppTopBar userName={userName} />

      <main className="mx-auto max-w-[880px] px-6 py-10">
        {/* Hero */}
        <header className="pb-8 border-b border-[var(--border)]">
          <p className="text-[12px] font-[600] uppercase tracking-[0.12em] text-[var(--color-gold-dark)] mb-3">
            {PRODUCT_NAME}
          </p>
          <h1
            className="font-serif font-[600] text-[40px] leading-[1.1] tracking-[-0.02em]"
            style={{ color: 'var(--color-near-black)', fontFamily: 'var(--font-serif)' }}
          >
            Welcome, {firstName}.
          </h1>
          <p className="text-[16px] text-[var(--color-charcoal)] mt-3 max-w-[60ch] leading-relaxed">
            Everything queued for you lives in your workspace — your daily checklist, the
            projects you own, and skills to build. Jump in below.
          </p>
        </header>

        {/* Your workspace */}
        <section aria-label="Your workspace" className="mt-10">
          <SectionHeader title="Your workspace" />
          <button
            type="button"
            onClick={() => navigate(DASHBOARD_ROUTE)}
            aria-label="Open your workspace"
            className={cn(
              'group block w-full text-left rounded-[4px] border bg-white overflow-hidden cursor-pointer',
              'transition-all duration-200',
              'shadow-[0_2px_8px_rgba(27,58,45,0.08)] hover:shadow-[0_4px_16px_rgba(27,58,45,0.12)]',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-gold)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-warm-white)]'
            )}
            style={{ borderColor: 'rgba(27,58,45,0.15)' }}
          >
            <div className="h-[3px] w-full" style={{ background: 'var(--color-gold)' }} aria-hidden />
            <div className="flex items-center gap-4 p-5">
              <div
                className="h-12 w-12 rounded-[6px] flex items-center justify-center shrink-0"
                style={{ background: 'var(--color-forest)' }}
                aria-hidden
              >
                <WorkspaceIcon className="h-6 w-6" style={{ color: 'var(--color-warm-white)' }} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3
                    className="text-[19px] font-[600] leading-snug"
                    style={{ color: 'var(--color-near-black)' }}
                  >
                    {workspaceName}
                  </h3>
                  <InternBadge variant="active">Open</InternBadge>
                </div>
                <p className="text-[12px] font-[600] uppercase tracking-[0.04em] text-[var(--color-gold-dark)] mt-1">
                  {workspaceTagline}
                </p>
                <p className="text-[15px] text-[var(--color-charcoal)] leading-relaxed mt-2">
                  {workspaceDescription}
                </p>
              </div>

              <ArrowRight
                className="h-5 w-5 shrink-0 transition-transform duration-200 group-hover:translate-x-1"
                style={{ color: 'var(--color-forest)' }}
                aria-hidden
              />
            </div>
          </button>
        </section>

        {/* Program context */}
        <section aria-label="Intern disciplines" className="mt-10">
          <SectionHeader title="The intern program" />
          <p className="text-[14px] text-[var(--color-charcoal)] mb-4 max-w-[60ch] leading-relaxed">
            XYZ runs interns across a few disciplines. Each intern gets their own workspace —
            checklist, projects, and skills tailored to them.
          </p>
          <div className="space-y-3">
            {DISCIPLINES.map((d) => {
              const isMine = d.id === track;
              const Icon = d.icon;
              return (
                <div
                  key={d.id}
                  className="rounded-[4px] border bg-white overflow-hidden"
                  style={{
                    borderColor: isMine ? 'rgba(27,58,45,0.25)' : 'rgba(27,58,45,0.12)',
                    opacity: isMine ? 1 : 0.7,
                  }}
                  role="article"
                  aria-label={d.name}
                >
                  <div className="flex items-center gap-4 p-4">
                    <div
                      className="h-10 w-10 rounded-[6px] flex items-center justify-center shrink-0"
                      style={{ background: isMine ? 'var(--color-forest)' : 'var(--muted)' }}
                      aria-hidden
                    >
                      <Icon
                        className="h-5 w-5"
                        style={{ color: isMine ? 'var(--color-warm-white)' : 'var(--color-mid-gray)' }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h3 className="text-[16px] font-[600]" style={{ color: 'var(--color-near-black)' }}>
                          {d.name}
                        </h3>
                        {isMine && <InternBadge variant="active">You</InternBadge>}
                      </div>
                      <p className="text-[12px] font-[600] uppercase tracking-[0.04em] text-[var(--color-gold-dark)] mt-0.5">
                        {d.tagline}
                      </p>
                      <p className="text-[14px] text-[var(--color-charcoal)] leading-relaxed mt-1">
                        {d.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="h-24" aria-hidden />
      </main>
    </div>
  );
}
